import { useEffect, useState, useMemo } from "react";
import { api, formatApiError, formatFCFA, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Download, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const CATEGORIES_IN = [
  { value: "cotisation", label: "Cotisation" },
  { value: "don", label: "Don" },
  { value: "subvention", label: "Subvention" },
  { value: "sponsoring", label: "Sponsoring" },
  { value: "billetterie", label: "Billetterie" },
  { value: "autre_entree", label: "Autre entrée" },
];
const CATEGORIES_OUT = [
  { value: "salaire", label: "Salaires" },
  { value: "materiel", label: "Matériel" },
  { value: "deplacement", label: "Déplacement" },
  { value: "restauration", label: "Restauration" },
  { value: "autre_sortie", label: "Autre sortie" },
];

const EMPTY = {
  type: "entree", category: "cotisation", amount: "",
  date: new Date().toISOString().slice(0, 10),
  description: "", payment_method: "espèces", related_to: "",
};

export default function Finances() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/finance").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    let income = 0, expense = 0;
    items.forEach((i) => i.type === "entree" ? income += i.amount : expense += i.amount);
    return { income, expense, balance: income - expense };
  }, [items]);

  const chartData = useMemo(() => {
    const byDate = {};
    [...items].reverse().forEach((i) => {
      const d = i.date;
      if (!byDate[d]) byDate[d] = { date: d, income: 0, expense: 0 };
      if (i.type === "entree") byDate[d].income += i.amount;
      else byDate[d].expense += i.amount;
    });
    return Object.values(byDate);
  }, [items]);

  const save = async () => {
    try {
      await api.post("/finance", { ...form, amount: Number(form.amount) });
      toast.success("Opération enregistrée");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette opération ?")) return;
    await api.delete(`/finance/${id}`);
    load();
  };

  const exportCSV = () => {
    const headers = ["Date", "Type", "Catégorie", "Montant", "Description", "Mode"];
    const rows = items.map((i) => [
      i.date, i.type, i.category, i.amount, i.description || "", i.payment_method || "",
    ]);
    const csv = [headers, ...rows].map((r) =>
      r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finances-amalvbc-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const allCategories = [...CATEGORIES_IN, ...CATEGORIES_OUT];

  return (
    <div data-testid="finances-page">
      <PageHeader title="Gestion financière" subtitle="Suivi des entrées, sorties et bilan du club">
        <Button onClick={exportCSV} variant="outline" data-testid="export-csv-button">
          <Download className="h-4 w-4 mr-2" /> Exporter CSV
        </Button>
        <Button onClick={() => setOpen(true)} data-testid="add-finance-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle opération
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={TrendingUp} label="Total entrées" value={formatFCFA(totals.income)}
            color="emerald" testid="total-income" />
          <StatCard icon={TrendingDown} label="Total sorties" value={formatFCFA(totals.expense)}
            color="amber" testid="total-expense" />
          <StatCard icon={Wallet} label="Solde net" value={formatFCFA(totals.balance)}
            color={totals.balance >= 0 ? "emerald" : "red"} testid="total-balance" />
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="finance-chart">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Flux financier</div>
            <h3 className="font-barlow font-bold text-xl tracking-tight mb-4">Historique chronologique</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00A859" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#00A859" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatFCFA(v)}
                    contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
                  <Area type="monotone" dataKey="income" stroke="#00A859" fill="url(#gIn)" strokeWidth={2} name="Entrées" />
                  <Area type="monotone" dataKey="expense" stroke="#F59E0B" fill="url(#gOut)" strokeWidth={2} name="Sorties" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full data-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Catégorie</th>
                <th className="text-left p-4">Description</th>
                <th className="text-right p-4">Montant</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="6" className="p-12 text-center text-slate-400">Aucune opération.</td></tr>
              ) : items.map((i) => (
                <tr key={i.id} data-testid={`finance-row-${i.id}`} className="border-b border-slate-100">
                  <td className="p-4 text-sm text-slate-600">{formatDate(i.date)}</td>
                  <td className="p-4">
                    <Badge className={i.type === "entree"
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-amber-100 text-amber-700 border-amber-200"}>
                      {i.type === "entree" ? "Entrée" : "Sortie"}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm capitalize text-slate-700">{i.category.replace("_", " ")}</td>
                  <td className="p-4 text-sm text-slate-600">{i.description || "—"}</td>
                  <td className={`p-4 text-right font-semibold font-mono ${i.type === "entree" ? "text-emerald-700" : "text-amber-700"}`}>
                    {i.type === "entree" ? "+" : "-"} {formatFCFA(i.amount)}
                  </td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="ghost" onClick={() => del(i.id)}
                      data-testid={`delete-finance-${i.id}`}
                      className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="finance-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">Nouvelle opération financière</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Type" value={form.type}
              onChange={(v) => setForm({ ...form, type: v, category: v === "entree" ? "cotisation" : "salaire" })}
              options={[
                { value: "entree", label: "Entrée d'argent" },
                { value: "sortie", label: "Sortie d'argent" },
              ]} testid="finance-type" />
            <SelectField label="Catégorie" value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={form.type === "entree" ? CATEGORIES_IN : CATEGORIES_OUT}
              testid="finance-category" />
            <Field label="Montant (FCFA)" type="number" value={form.amount}
              onChange={(v) => setForm({ ...form, amount: v })} required testid="finance-amount" />
            <Field label="Date" type="date" value={form.date}
              onChange={(v) => setForm({ ...form, date: v })} testid="finance-date" />
            <SelectField label="Mode de paiement" value={form.payment_method}
              onChange={(v) => setForm({ ...form, payment_method: v })}
              options={[
                { value: "espèces", label: "Espèces" },
                { value: "virement", label: "Virement" },
                { value: "mobile_money", label: "Mobile Money" },
                { value: "chèque", label: "Chèque" },
              ]} testid="finance-method" />
            <Field label="Référence (optionnel)" value={form.related_to}
              onChange={(v) => setForm({ ...form, related_to: v })} testid="finance-ref" />
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">
                Description
              </Label>
              <Textarea data-testid="finance-description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-finance-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  const palettes = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    red: "text-red-700 bg-red-50 border-red-200",
  };
  return (
    <div data-testid={testid} className={`stat-card bg-white border border-slate-200 rounded-md p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <span className={`p-2 rounded-md border ${palettes[color]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="font-barlow font-bold text-3xl tracking-tight text-slate-900 leading-none mt-3">{value}</div>
    </div>
  );
}
